/* eslint-disable import/no-anonymous-default-export */
import React, { useContext, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { api } from "../../config/api";
import "./myPage.scss";
import AlertContext from "../../contexts/AlertContext";
import { Spin, Card, Button, Empty, Modal, Form, Input, Select, Row, Col, message } from "antd";
import { CreditCardOutlined, DeleteOutlined, PlusOutlined, CalendarOutlined } from "@ant-design/icons";

interface BillingInfo {
  billing_key: string;
  card_brand: string;
  card_last4: string;
  card_name: string;
  created_at: string;
  is_active: boolean;
}

interface SubscriptionInfo {
  subscription_start_date: string;
  next_payment_date: string | null;
  card_info?: {
    card_brand: string;
    card_last4: string;
  };
}

interface ActivePackage {
  product_id: number;
  product_name: string;
  monthly_amount: number;
  is_yearly: boolean;
  next_payment_date: string | null;
  subscription_start_date: string;
  schedule_id: string;
  // Netflix-style cancellation
  is_active: boolean;
  cancel_requested: boolean;
  cancel_requested_at?: string | null;
  expiry_date?: string | null;
  // 살아있는 결제 예약 여부. cancel_requested가 스테일이어도 이 값이 true면
  // 실제로는 계속 청구되므로 해지 수단을 노출해야 한다.
  has_pending_schedule?: boolean;
}

interface SubscriptionStatus {
  subscription_active: boolean;
  subscription_info?: SubscriptionInfo;
  active_packages?: ActivePackage[];
  recent_payments?: Array<{
    payment_id: string;
    amount: number;
    paid_at: string | null;
    status: string;
  }>;
}

interface Props {}

export default () => {
  const [loading, setLoading] = useState(false);
  const [billingInfo, setBillingInfo] = useState<BillingInfo | null>(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState<SubscriptionStatus | null>(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isUpdateModalVisible, setIsUpdateModalVisible] = useState(false);
  const [isCancelModalVisible, setIsCancelModalVisible] = useState(false);
  const [showMigrationNotice, setShowMigrationNotice] = useState(false);
  const [form] = Form.useForm();
  const [updateForm] = Form.useForm();
  const [cancelForm] = Form.useForm();
  const alert = useContext(AlertContext);
  const history = useHistory();

  useEffect(() => {
    console.log('🔍 MyPagePaymentMethod 마운트됨');
    loadBillingInfo();
    loadSubscriptionStatus();
  }, []);

  useEffect(() => {
    console.log('🔍 subscriptionStatus 상태 변경:', subscriptionStatus);
  }, [subscriptionStatus]);

  const loadBillingInfo = async () => {
    setLoading(true);
    try {
      const response = await api.getSubscriptionBillingAsync();
      if (response.data.code === 1 && response.data.billing_info) {
        setBillingInfo(response.data.billing_info);
        setShowMigrationNotice(false);
      } else {
        // 카드 정보 없음
        setBillingInfo(null);
        // PG 변경 안내 표시 여부 확인
        if (response.data.show_pg_migration_notice) {
          setShowMigrationNotice(true);
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const loadSubscriptionStatus = async () => {
    console.log('🔍 loadSubscriptionStatus 시작');
    try {
      const response = await api.getSubscriptionStatusAsync();
      console.log('🔍 API 응답:', response.data);
      if (response.data.code === 1) {
        console.log('🔍 구독 활성:', response.data.subscription_active);
        console.log('🔍 active_packages:', response.data.active_packages);
        setSubscriptionStatus({
          subscription_active: response.data.subscription_active,
          subscription_info: response.data.subscription_info,
          active_packages: response.data.active_packages,
          recent_payments: response.data.recent_payments
        });
      } else {
        console.log('🔍 응답 코드가 1이 아님:', response.data.code);
      }
    } catch (error) {
      console.error('🔍 loadSubscriptionStatus 에러:', error);
    }
  };

  const handleRegisterCard = async (values: any) => {
    setLoading(true);
    try {
      // 생년월일 또는 사업자등록번호 처리
      const requestData = {
        card_number: values.card_number.replace(/\D/g, ''), // 하이픈 제거
        expiry_month: values.expiry_month,
        expiry_year: values.expiry_year,
        pwd_2digit: values.pwd_2digit,
      };

      if (values.card_type === 'personal') {
        requestData['birth'] = values.birth;
      } else {
        requestData['business_num'] = values.business_num;
      }

      const response = await api.registerBillingKeyAsync(requestData);

      if (response.data.code === 1) {
        message.success('카드가 성공적으로 등록되었습니다.');
        setIsModalVisible(false);
        form.resetFields();
        await loadBillingInfo();
      } else {
        alert.showAlert(response.data.message || '카드 등록에 실패했습니다.');
      }
    } catch (error: any) {
      console.error(error);
      alert.showAlert(error.response?.data?.message || '카드 등록 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateCard = async (values: any) => {
    setLoading(true);
    try {
      // 생년월일 또는 사업자등록번호 처리
      const requestData = {
        card_number: values.card_number.replace(/\D/g, ''), // 하이픈 제거
        expiry_month: values.expiry_month,
        expiry_year: values.expiry_year,
        pwd_2digit: values.pwd_2digit,
      };

      if (values.card_type === 'personal') {
        requestData['birth'] = values.birth;
      } else {
        requestData['business_num'] = values.business_num;
      }

      const response = await api.updateSubscriptionBillingAsync(requestData);

      if (response.data.code === 1) {
        message.success('카드가 성공적으로 변경되었습니다.');
        setIsUpdateModalVisible(false);
        updateForm.resetFields();
        // 밀려 있던(결제 실패) 구독 결제는 카드를 바꾸면 새 카드로 바로 결제된다 — 결과를 알려 준다
        const su = response.data.schedule_update || {};
        if (su.immediate_paid > 0) {
          alert.showAlert(`밀려 있던 구독 결제 ${su.immediate_paid}건을 새 카드로 결제했습니다. 이용권이 다시 열렸어요.`);
        } else if (su.immediate_failed > 0) {
          alert.showAlert('새 카드로도 밀린 구독 결제가 되지 않았습니다. 카드 한도·잔액을 확인하시거나 다른 카드로 바꿔 주세요.');
        } else if (su.blocked > 0 || su.immediate_unknown > 0) {
          alert.showAlert('자동 재결제가 진행 중이라 지금 바로 결제하지 않았습니다. 잠시 뒤 결제 내역을 확인해 주세요.');
        }
        await loadBillingInfo();
      } else {
        alert.showAlert(response.data.message || '카드 변경에 실패했습니다.');
      }
    } catch (error: any) {
      console.error(error);
      alert.showAlert(error.response?.data?.message || '카드 변경 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCard = async () => {
    // 활성 구독이 있는지 확인
    if (subscriptionStatus?.subscription_active) {
      alert.showAlert(
        '정기결제가 진행 중입니다.\n' +
        '구독을 먼저 해지하거나 카드 변경 기능을 이용해주세요.'
      );
      return;
    }

    const confirmed = window.confirm('등록된 카드를 삭제하시겠습니까?');
    if (!confirmed) return;

    setLoading(true);
    try {
      const response = await api.deleteSubscriptionBillingAsync();
      if (response.data.code === 1) {
        message.success('카드가 삭제되었습니다.');
        setBillingInfo(null);
        await loadSubscriptionStatus();
      } else {
        alert.showAlert(response.data.message || '카드 삭제에 실패했습니다.');
      }
    } catch (error: any) {
      console.error(error);
      alert.showAlert(error.response?.data?.message || '카드 삭제 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const [selectedProductId, setSelectedProductId] = useState<number | null>(null);
  const [selectedIsYearly, setSelectedIsYearly] = useState<boolean | undefined>(undefined);

  const handleCancelSubscription = (productId?: number, isYearly?: boolean) => {
    setSelectedProductId(productId || null);
    setSelectedIsYearly(isYearly);
    setIsCancelModalVisible(true);
  };

  const handleConfirmCancellation = async (values: any) => {
    setLoading(true);
    try {
      const response = await api.postSubscriptionCancelAsync({
        product_id: selectedProductId,
        is_yearly: selectedIsYearly,
        cancellation_reason: values.cancellation_reason,
        cancellation_detail: values.cancellation_detail || ''
      });

      if (response.data.code === 1) {
        setIsCancelModalVisible(false);
        cancelForm.resetFields();
        await loadSubscriptionStatus();

        Modal.success({
          title: '구독 해지 완료',
          content: (
            <div>
              <p>정기결제가 성공적으로 해지되었습니다.</p>
              <p className="text-muted small mb-0">다음 결제일까지 서비스를 계속 이용하실 수 있습니다.</p>
            </div>
          ),
        });
      } else {
        alert.showAlert(response.data.message || '정기결제 해지에 실패했습니다.');
      }
    } catch (error: any) {
      console.error(error);
      alert.showAlert(error.response?.data?.message || '정기결제 해지 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleResumeSubscription = async (productId: number, productName: string, isYearly?: boolean) => {
    Modal.confirm({
      title: '구독 재개',
      content: `${productName} 구독을 재개하시겠습니까?`,
      okText: '재개',
      cancelText: '취소',
      onOk: async () => {
        setLoading(true);
        try {
          const response = await api.postSubscriptionResumeAsync({
            product_id: productId,
            is_yearly: isYearly
          });

          if (response.data.code === 1) {
            await loadSubscriptionStatus();
            Modal.success({
              title: '구독 재개 완료',
              content: (
                <div>
                  <p>{productName} 구독이 재개되었습니다.</p>
                  <p className="text-muted small mb-0">다음 결제일: {new Date(response.data.data.next_payment_date).toLocaleDateString('ko-KR')}</p>
                </div>
              ),
            });
          } else {
            alert.showAlert(response.data.message || '구독 재개에 실패했습니다.');
          }
        } catch (error: any) {
          console.error(error);
          const errorData = error.response?.data;

          // 카드 등록 필요한 경우
          if (errorData?.require_card_registration) {
            Modal.confirm({
              title: '카드 등록 필요',
              content: (
                <div>
                  <p>{errorData.message || '카드를 먼저 등록해주세요.'}</p>
                  <p className="text-muted small">결제대행사 변경으로 인해 카드를 새로 등록해야 합니다.</p>
                </div>
              ),
              okText: '카드 등록하기',
              cancelText: '취소',
              onOk: () => {
                setIsModalVisible(true);
              }
            });
          } else {
            alert.showAlert(errorData?.message || '구독 재개 중 오류가 발생했습니다.');
          }
        } finally {
          setLoading(false);
        }
      }
    });
  };

  const formatCardBrand = (cardName: string) => {
    // 카드사 정보에서 브랜드 추출 시도
    if (cardName.includes('VISA')) return 'VISA';
    if (cardName.includes('MASTER')) return 'MasterCard';
    if (cardName.includes('AMEX')) return 'AMEX';
    return '';
  };

  // 카드번호 포맷팅 (하이픈 자동 추가)
  // 일반 카드: 16자리 (4-4-4-4), AMEX: 15자리 (4-4-4-3)
  const formatCardNumber = (value: string) => {
    const numbers = value.replace(/\D/g, ''); // 숫자만 추출
    const parts = numbers.match(/.{1,4}/g) || []; // 4자리씩 분할
    return parts.join('-').substr(0, 19); // 최대 16자리 + 3개 하이픈 = 19자 (AMEX는 15+3=18자)
  };

  // 카드번호 입력 핸들러
  const handleCardNumberChange = (e: React.ChangeEvent<HTMLInputElement>, formInstance: any) => {
    const formatted = formatCardNumber(e.target.value);
    formInstance.setFieldsValue({ card_number: formatted });
  };

  // PG 변경 안내 배너
  const PgMigrationNotice = () => (
    <Card
      className="mb-4"
      style={{
        borderLeft: '4px solid #faad14',
        backgroundColor: '#fffbe6'
      }}
    >
      <div className="d-flex align-items-start">
        <span style={{ fontSize: 24, marginRight: 12 }}>⚠️</span>
        <div>
          <h5 style={{ color: '#d48806', marginBottom: 8 }}>결제대행사 변경 안내</h5>
          <p style={{ marginBottom: 0, color: '#5d4037' }}>
            결제 서비스 개선을 위해 결제대행사가 변경되었습니다.
            <br />
            기존 카드로는 신규 구독 및 구독 재개가 불가능하오니,
            <br />
            <strong>카드를 새로 등록</strong>해 주시기 바랍니다.
          </p>
        </div>
      </div>
    </Card>
  );

  return (
    <section className="content">
      <h3>구독 관리</h3>

      {/* 구독 중인 상품 정보 */}
      {subscriptionStatus?.subscription_active && subscriptionStatus.active_packages && subscriptionStatus.active_packages.length > 0 ? (
        <Card className="mb-4" style={{ borderLeft: '4px solid #1890ff' }}>
          <div className="d-flex align-items-center mb-3">
            <h4 className="mb-0" style={{ color: '#1890ff', marginRight: 12 }}>
              구독 중인 서비스
            </h4>
            {/* 전체 상태 배지: 하나라도 active가 있으면 진행중, 모두 취소됐으면 종료 예정 */}
            {subscriptionStatus.active_packages.some(pkg => pkg.is_active && !pkg.cancel_requested) ? (
              <span className="badge bg-success">정기결제 진행중</span>
            ) : (
              <span className="badge bg-warning text-dark">종료 예정</span>
            )}
          </div>

          {subscriptionStatus.active_packages.map((pkg, index) => (
            <div key={pkg.product_id} className={index > 0 ? 'mt-3 pt-3' : ''} style={index > 0 ? { borderTop: '1px solid #f0f0f0' } : {}}>
              <div className="row align-items-center">
                <div className="col-md-3 mb-2">
                  <div className="text-muted small">상품명</div>
                  <div className="fw-bold">
                    {pkg.product_name}
                    {/* 개별 상품 취소 상태 배지 */}
                    {pkg.cancel_requested && (
                      <span className="badge bg-warning text-dark ms-2" style={{ fontSize: '0.7em' }}>
                        {/* 예약이 살아있으면 실제로는 계속 결제된다. "종료 예정"이라고만
                            표시하면 고객이 해지된 줄 알고 그대로 청구받는다. */}
                        {pkg.has_pending_schedule ? '결제 예정' : '종료 예정'}
                      </span>
                    )}
                  </div>
                </div>
                <div className="col-md-2 mb-2">
                  <div className="text-muted small">결제금액</div>
                  <div className="fw-bold">
                    {pkg.monthly_amount.toLocaleString()}원
                    {pkg.is_yearly && <span className="text-muted small"> (연간)</span>}
                  </div>
                </div>
                <div className="col-md-3 mb-2">
                  <div className="text-muted small">
                    {pkg.cancel_requested && !pkg.has_pending_schedule ? '서비스 종료일' : '다음 결제일'}
                  </div>
                  <div className="fw-bold">
                    {pkg.cancel_requested && !pkg.has_pending_schedule && pkg.expiry_date
                      ? new Date(pkg.expiry_date).toLocaleDateString('ko-KR')
                      : pkg.next_payment_date
                      ? new Date(pkg.next_payment_date).toLocaleDateString('ko-KR')
                      : '미정'}
                  </div>
                </div>
                <div className="col-md-4 mb-2 text-end">
                  {/*
                    cancel_requested만 보고 "구독 중단" 버튼을 감추면 안 된다.
                    해지 후 재구독한 고객에게 옛 플래그가 남으면 버튼이 사라지는데
                    결제 예약은 살아있어, 고객이 구독을 멈출 방법이 완전히 없어진다.
                    예약이 남아 있으면 반드시 중단 수단을 남겨 둔다.
                  */}
                  {pkg.cancel_requested && !pkg.has_pending_schedule ? (
                    <div className="d-flex flex-column align-items-end">
                      <Button
                        type="primary"
                        onClick={() => handleResumeSubscription(pkg.product_id, pkg.product_name, pkg.is_yearly)}
                        size="middle"
                      >
                        다시 구독하기
                      </Button>
                      <div className="text-muted small mt-2">
                        만료일까지 이용 가능
                      </div>
                    </div>
                  ) : (
                    <Button
                      type="primary"
                      danger
                      onClick={() => handleCancelSubscription(pkg.product_id, pkg.is_yearly)}
                      size="middle"
                    >
                      구독 중단
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </Card>
      ) : (
        <Card className="mb-4">
          <Empty
            description="구독 중인 상품이 없습니다"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        </Card>
      )}

      {/* PG 변경 안내 배너 */}
      {showMigrationNotice && <PgMigrationNotice />}

      {/* 결제 수단 정보 */}
      <h4 className="mt-4 mb-3">등록된 결제 수단</h4>

      <div className="mt-4">
        {loading ? (
          <div className="loading-container">
            <Spin size="large" />
          </div>
        ) : billingInfo ? (
          <Card className="payment-method-card">
            <div className="d-flex justify-content-between align-items-start">
              <div className="d-flex align-items-start">
                <CreditCardOutlined style={{ fontSize: 48, color: '#1890ff', marginRight: 20 }} />
                <div>
                  <h4 className="mb-1">
                    ****-****-****-{billingInfo.card_last4}
                  </h4>
                  <div className="text-muted">
                    {formatCardBrand(billingInfo.card_name) && (
                      <span className="me-3">{formatCardBrand(billingInfo.card_name)}</span>
                    )}
                    <CalendarOutlined className="me-1" />
                    {new Date(billingInfo.created_at).toLocaleDateString('ko-KR')} 등록
                  </div>
                </div>
              </div>
              <div className="d-flex gap-2 align-items-center">
                <Button
                  type="default"
                  icon={<CreditCardOutlined style={{ verticalAlign: 'middle' }} />}
                  onClick={() => setIsUpdateModalVisible(true)}
                  loading={loading}
                  style={{ display: 'inline-flex', alignItems: 'center' }}
                >
                  카드 변경
                </Button>
                <Button
                  type="primary"
                  danger
                  icon={<DeleteOutlined style={{ verticalAlign: 'middle' }} />}
                  onClick={handleDeleteCard}
                  loading={loading}
                  style={{ display: 'inline-flex', alignItems: 'center' }}
                >
                  카드 삭제
                </Button>
              </div>
            </div>
          </Card>
        ) : (
          <Card>
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="등록된 결제 수단이 없습니다"
            >
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => setIsModalVisible(true)}
              >
                카드 등록하기
              </Button>
            </Empty>
          </Card>
        )}
      </div>

      <div className="mt-4">
        <Card title="안내사항" size="small">
          <ul style={{ paddingLeft: 20, marginBottom: 0 }}>
            <li>등록된 카드는 정기결제에 사용됩니다.</li>
            <li>카드 정보는 안전하게 암호화되어 저장됩니다.</li>
            <li>카드 변경 버튼을 이용하여 카드를 변경할 수 있습니다.</li>
            <li>정기결제가 진행 중인 경우, 카드 삭제가 제한됩니다. 먼저 구독을 해지하거나 카드 변경 기능을 이용해주세요.</li>
          </ul>
        </Card>
      </div>

      <Modal
        title="카드 등록"
        visible={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          form.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleRegisterCard}
          initialValues={{ card_type: 'personal' }}
        >
          <Form.Item
            name="card_number"
            label="카드번호"
            extra="일반 카드: 16자리, AMEX: 15자리"
            rules={[
              { required: true, message: '카드번호를 입력해주세요' },
              {
                validator: (_, value) => {
                  const numbers = value?.replace(/\D/g, '') || '';
                  if (numbers.length < 15 || numbers.length > 16) {
                    return Promise.reject('15-16자리 숫자를 입력해주세요');
                  }
                  return Promise.resolve();
                }
              }
            ]}
          >
            <Input
              placeholder="1234-5678-1234-5678"
              maxLength={19}
              onChange={(e) => handleCardNumberChange(e, form)}
              autoComplete="one-time-code"
              name="not_a_card_number_register"
            />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="expiry_month"
                label="유효기간 (월)"
                rules={[
                  { required: true, message: '월을 입력해주세요' },
                  { pattern: /^(0[1-9]|1[0-2])$/, message: '01-12 형식으로 입력해주세요' }
                ]}
              >
                <Input
                  placeholder="MM"
                  maxLength={2}
                  autoComplete="one-time-code"
                  name="not_expiry_month_register"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="expiry_year"
                label="유효기간 (년)"
                rules={[
                  { required: true, message: '년도를 입력해주세요' },
                  { pattern: /^[0-9]{2}$/, message: '2자리 숫자를 입력해주세요' }
                ]}
              >
                <Input
                  placeholder="YY"
                  maxLength={2}
                  autoComplete="one-time-code"
                  name="not_expiry_year_register"
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="card_type"
            label="카드 구분"
            rules={[{ required: true, message: '카드 구분을 선택해주세요' }]}
          >
            <Select getPopupContainer={(trigger) => trigger.parentElement}>
              <Select.Option value="personal">개인카드</Select.Option>
              <Select.Option value="business">법인카드</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) => prevValues.card_type !== currentValues.card_type}
          >
            {({ getFieldValue }) =>
              getFieldValue('card_type') === 'personal' ? (
                <Form.Item
                  name="birth"
                  label="생년월일 (6자리)"
                  rules={[
                    { required: true, message: '생년월일을 입력해주세요' },
                    { pattern: /^[0-9]{6}$/, message: '6자리 숫자를 입력해주세요' }
                  ]}
                >
                  <Input
                    placeholder="YYMMDD"
                    maxLength={6}
                    autoComplete="one-time-code"
                    name="not_birth_register"
                  />
                </Form.Item>
              ) : (
                <Form.Item
                  name="business_num"
                  label="사업자등록번호 (10자리)"
                  rules={[
                    { required: true, message: '사업자등록번호를 입력해주세요' },
                    { pattern: /^[0-9]{10}$/, message: '10자리 숫자를 입력해주세요' }
                  ]}
                >
                  <Input
                    placeholder="1234567890"
                    maxLength={10}
                    autoComplete="one-time-code"
                    name="not_business_register"
                  />
                </Form.Item>
              )
            }
          </Form.Item>

          <Form.Item
            name="pwd_2digit"
            label="카드 비밀번호 앞 2자리"
            rules={[
              { required: true, message: '비밀번호 앞 2자리를 입력해주세요' },
              { pattern: /^[0-9]{2}$/, message: '2자리 숫자를 입력해주세요' }
            ]}
          >
            <Input
              placeholder="**"
              maxLength={2}
              autoComplete="one-time-code"
              name="not_a_password_register"
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, marginTop: 24 }}>
            <Button type="primary" htmlType="submit" loading={loading} block>
              카드 등록
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* 카드 변경 모달 */}
      <Modal
        title="카드 변경"
        visible={isUpdateModalVisible}
        onCancel={() => {
          setIsUpdateModalVisible(false);
          updateForm.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form
          form={updateForm}
          layout="vertical"
          onFinish={handleUpdateCard}
          initialValues={{ card_type: 'personal' }}
        >
          <Form.Item
            name="card_number"
            label="카드번호"
            extra="일반 카드: 16자리, AMEX: 15자리"
            rules={[
              { required: true, message: '카드번호를 입력해주세요' },
              {
                validator: (_, value) => {
                  const numbers = value?.replace(/\D/g, '') || '';
                  if (numbers.length < 15 || numbers.length > 16) {
                    return Promise.reject('15-16자리 숫자를 입력해주세요');
                  }
                  return Promise.resolve();
                }
              }
            ]}
          >
            <Input
              placeholder="1234-5678-1234-5678"
              maxLength={19}
              onChange={(e) => handleCardNumberChange(e, updateForm)}
              autoComplete="one-time-code"
              name="not_a_card_number_update"
            />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="expiry_month"
                label="유효기간 (월)"
                rules={[
                  { required: true, message: '월을 입력해주세요' },
                  { pattern: /^(0[1-9]|1[0-2])$/, message: '01-12 형식으로 입력해주세요' }
                ]}
              >
                <Input
                  placeholder="MM"
                  maxLength={2}
                  autoComplete="one-time-code"
                  name="not_expiry_month_update"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="expiry_year"
                label="유효기간 (년)"
                rules={[
                  { required: true, message: '년도를 입력해주세요' },
                  { pattern: /^[0-9]{2}$/, message: '2자리 숫자를 입력해주세요' }
                ]}
              >
                <Input
                  placeholder="YY"
                  maxLength={2}
                  autoComplete="one-time-code"
                  name="not_expiry_year_update"
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="card_type"
            label="카드 구분"
            rules={[{ required: true, message: '카드 구분을 선택해주세요' }]}
          >
            <Select getPopupContainer={(trigger) => trigger.parentElement}>
              <Select.Option value="personal">개인카드</Select.Option>
              <Select.Option value="business">법인카드</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) => prevValues.card_type !== currentValues.card_type}
          >
            {({ getFieldValue }) =>
              getFieldValue('card_type') === 'personal' ? (
                <Form.Item
                  name="birth"
                  label="생년월일 (6자리)"
                  rules={[
                    { required: true, message: '생년월일을 입력해주세요' },
                    { pattern: /^[0-9]{6}$/, message: '6자리 숫자를 입력해주세요' }
                  ]}
                >
                  <Input
                    placeholder="YYMMDD"
                    maxLength={6}
                    autoComplete="one-time-code"
                    name="not_birth_update"
                  />
                </Form.Item>
              ) : (
                <Form.Item
                  name="business_num"
                  label="사업자등록번호 (10자리)"
                  rules={[
                    { required: true, message: '사업자등록번호를 입력해주세요' },
                    { pattern: /^[0-9]{10}$/, message: '10자리 숫자를 입력해주세요' }
                  ]}
                >
                  <Input
                    placeholder="1234567890"
                    maxLength={10}
                    autoComplete="one-time-code"
                    name="not_business_update"
                  />
                </Form.Item>
              )
            }
          </Form.Item>

          <Form.Item
            name="pwd_2digit"
            label="카드 비밀번호 앞 2자리"
            rules={[
              { required: true, message: '비밀번호 앞 2자리를 입력해주세요' },
              { pattern: /^[0-9]{2}$/, message: '2자리 숫자를 입력해주세요' }
            ]}
          >
            <Input
              placeholder="**"
              maxLength={2}
              autoComplete="one-time-code"
              name="not_a_password_update"
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, marginTop: 24 }}>
            <Button type="primary" htmlType="submit" loading={loading} block>
              카드 변경
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* 정기결제 해지 사유 선택 모달 */}
      <Modal
        title="정기결제 해지"
        visible={isCancelModalVisible}
        onCancel={() => {
          setIsCancelModalVisible(false);
          cancelForm.resetFields();
        }}
        footer={null}
        width={600}
      >
        {/* 취소 정책 안내 */}
        <div className="mb-4" style={{
          padding: '16px',
          backgroundColor: '#f0f5ff',
          borderRadius: '8px',
          border: '1px solid #d6e4ff'
        }}>
          <h4 style={{ color: '#1890ff', marginBottom: '12px' }}>📌 구독 중단 안내</h4>
          <ul style={{ marginBottom: 0, paddingLeft: '20px' }}>
            <li style={{ marginBottom: '8px' }}>
              <strong>다음 결제일까지 계속 사용 가능합니다</strong>
              {subscriptionStatus?.subscription_info?.next_payment_date && (
                <div style={{ color: '#666', fontSize: '14px' }}>
                  ({new Date(subscriptionStatus.subscription_info.next_payment_date).toLocaleDateString('ko-KR')}까지 이용 가능)
                </div>
              )}
            </li>
            <li style={{ marginBottom: '8px' }}>다음 결제는 자동으로 중단됩니다</li>
            <li style={{ marginBottom: 0 }}>환불은 고객센터로 문의해주세요</li>
          </ul>
        </div>

        <div className="mb-3">
          <p>정기결제를 해지하는 이유를 알려주세요. 더 나은 서비스 제공을 위해 활용하겠습니다.</p>
        </div>
        
        <Form
          form={cancelForm}
          layout="vertical"
          onFinish={handleConfirmCancellation}
        >
          <Form.Item
            name="cancellation_reason"
            label="해지 사유"
            rules={[{ required: true, message: '해지 사유를 선택해주세요' }]}
          >
            <Select
              placeholder="해지 사유를 선택해주세요"
              getPopupContainer={(trigger) => trigger.parentNode as HTMLElement}
            >
              <Select.Option value="price_burden">가격 부담</Select.Option>
              <Select.Option value="service_dissatisfaction">서비스 불만족</Select.Option>
              <Select.Option value="low_usage">사용 빈도 낮음</Select.Option>
              <Select.Option value="other_service">다른 서비스 이용</Select.Option>
              <Select.Option value="feature_lack">기능 부족</Select.Option>
              <Select.Option value="difficult_usage">사용법 어려움</Select.Option>
              <Select.Option value="support_dissatisfaction">고객지원 불만</Select.Option>
              <Select.Option value="other">기타</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="cancellation_detail"
            label="상세 사유 (선택사항)"
          >
            <Input.TextArea
              rows={4}
              placeholder="추가로 알려주고 싶은 내용이 있다면 자유롭게 작성해주세요."
              maxLength={500}
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, marginTop: 24 }}>
            <Row gutter={8}>
              <Col span={12}>
                <Button 
                  block 
                  onClick={() => {
                    setIsCancelModalVisible(false);
                    cancelForm.resetFields();
                  }}
                >
                  취소
                </Button>
              </Col>
              <Col span={12}>
                <Button type="primary" danger htmlType="submit" loading={loading} block>
                  해지하기
                </Button>
              </Col>
            </Row>
          </Form.Item>
        </Form>
      </Modal>
    </section>
  );
};